/**
 * Unified Firebase Initialization for Kent Traders
 * Initializes Firebase for both Loyalty and Alert systems
 * This ensures consistent configuration across all services
 */

(function() {
  'use strict';

  // Import unified Firebase configuration
  // <script src="/config/unified-firebase-config.js"></script>

  // Unified Firebase Configuration - Now uses unified config
  const FIREBASE_CONFIG = window.UNIFIED_FIREBASE_CONFIG || {
    apiKey: "",
    authDomain: "",
    projectId: "",
    storageBucket: "",
    messagingSenderId: "",
    appId: ""
    // measurementId removed to avoid conflicts
  };

  // Service configurations
  const SERVICES_CONFIG = {
    loyalty: {
      functions: {
        getCustomerLoyalty: "getCustomerLoyalty",
        updateCustomerPoints: "updateCustomerPoints",
        redeemPoints: "redeemPoints",
        getLoyaltySummary: "getLoyaltySummary"
      },
      collections: {
        customers: "loyalty_customers",
        transactions: "loyalty_transactions"
      }
    },
    alerts: {
      functions: {
        sendEmailNotification: "sendEmailNotification",
        sendPushNotification: "sendPushNotification",
        getVapidPublicKey: "getVapidPublicKey",
        processBackInStockAlerts: "processBackInStockAlerts",
        processPriceDropAlerts: "processPriceDropAlerts"
      },
      collections: {
        subscriptions: "alert_subscriptions",
        analytics: "alert_analytics"
      },
      vapidKeys: {
        publicKey: "BDOo1TaAfO8bKPUZ5HaTsH4mrRmjLjhcOjqnVtLrr-6jDvkaQpHJz09GUKcF9G4Ocn-3ayDJnjbr2_bDs7tqEDM"
      }
    }
  };

  class UnifiedFirebaseManager {
    constructor() {
      this.config = FIREBASE_CONFIG;
      this.services = SERVICES_CONFIG;
      this.initialized = false;
      this.app = null;
      this.db = null;
      this.auth = null;
      this.functions = null;
      this.analytics = null;
      this.functionsBaseUrl = "";
      
      // Rate limiting and request tracking
      this.requestQueue = [];
      this.lastRequestTime = 0;
      this.minRequestInterval = 100; // Minimum 100ms between requests
      this.maxConcurrentRequests = 5;
      this.activeRequests = 0;
      this.requestTimeout = 30000; // 30 seconds timeout
    }

    /**
     * Update configuration from environment variables
     */
    updateConfig() {
      if (window.__ENV__) {
        this.config = {
          apiKey: window.__ENV__.FIREBASE_API_KEY || "",
          authDomain: window.__ENV__.FIREBASE_AUTH_DOMAIN || "",
          projectId: window.__ENV__.FIREBASE_PROJECT_ID || "",
          storageBucket: window.__ENV__.FIREBASE_STORAGE_BUCKET || "",
          messagingSenderId: window.__ENV__.FIREBASE_MESSAGING_SENDER_ID || "",
          appId: window.__ENV__.FIREBASE_APP_ID || ""
        };
        
        this.functionsBaseUrl = `https://us-central1-${window.__ENV__.FIREBASE_PROJECT_ID || ''}.cloudfunctions.net`;
      }
    }

    /**
     * Initialize Firebase
     */
    async initialize() {
      if (this.initialized) {
        return this;
      }

      try {
        // Wait for secure configuration to load if not ready
        if (!window.isConfigReady || !window.isConfigReady()) {
          if (window.waitForConfig) {
            await window.waitForConfig();
            this.updateConfig();
          } else {
            // Fallback: wait a bit for config to load
            await new Promise(resolve => setTimeout(resolve, 1000));
            this.updateConfig();
          }
        } else {
          this.updateConfig();
        }

        // Validate configuration
        if (!this.config.apiKey || !this.config.authDomain || !this.config.projectId) {
          throw new Error('Firebase configuration not ready. Missing required values.');
        }

        // Import Firebase modules
        const { initializeApp } = await import('https://www.gstatic.com/firebasejs/10.7.0/firebase-app.js');
        const { getFirestore } = await import('https://www.gstatic.com/firebasejs/10.7.0/firebase-firestore.js');
        const { getAuth } = await import('https://www.gstatic.com/firebasejs/10.7.0/firebase-auth.js');
        const { getFunctions } = await import('https://www.gstatic.com/firebasejs/10.7.0/firebase-functions.js');

        // Initialize Firebase app
        this.app = initializeApp(this.config);
        this.db = getFirestore(this.app);
        this.auth = getAuth(this.app);
        this.functions = getFunctions(this.app);

        // Initialize analytics if available
        try {
          const { getAnalytics } = await import('https://www.gstatic.com/firebasejs/10.7.0/firebase-analytics.js');
          this.analytics = getAnalytics(this.app);
        } catch (error) {
          // Analytics not available - silent fail (API key may be invalid, which is OK)
          if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
            // Only log in production if it's not an API key error
            if (!error.message || !error.message.includes('API key')) {
              console.warn('Firebase Analytics initialization skipped:', error.message);
            }
          }
        }

        this.initialized = true;

        // Dispatch ready event
        this.dispatchEvent('firebaseReady', { manager: this });

        return this;
      } catch (error) {
        // Fail gracefully - Firebase may not be configured (API key missing/invalid)
        const isApiKeyError = error.message && (
          error.message.includes('API key') || 
          error.message.includes('apiKey') ||
          error.message.includes('config')
        );
        
        if (isApiKeyError) {
          // API key errors are expected if Firebase is not configured - silent fail
          if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            console.warn('🔥 Firebase not configured (API key missing). This is expected in development.');
          }
        } else {
          // Other errors should be logged
          console.error('🔥 Unified Firebase Manager: Initialization failed:', error);
        }
        
        // Don't throw - allow app to continue without Firebase
        this.initialized = false;
        return this;
      }
    }

    /**
     * Validate function call parameters
     * @param {string} functionName - The name of the Firebase function to call
     * @param {Object|null} data - The data to pass to the function
     * @throws {Error} If validation fails
     * @returns {boolean} True if validation passes
     */
    _validateFunctionCall(functionName, data) {
      if (!functionName || typeof functionName !== 'string') {
        throw new Error('Function name must be a non-empty string');
      }
      
      // Validate function name format (alphanumeric, hyphens, underscores only)
      if (!/^[a-zA-Z0-9_-]+$/.test(functionName)) {
        throw new Error('Invalid function name format');
      }
      
      // Validate data is an object
      if (data !== null && typeof data !== 'object') {
        throw new Error('Data must be an object or null');
      }
      
      // Validate customerId if present
      if (data && data.customerId !== undefined) {
        if (typeof data.customerId !== 'string' || data.customerId.trim().length === 0) {
          throw new Error('Invalid customerId: must be a non-empty string');
        }
      }
      
      // Validate points if present
      if (data && data.points !== undefined) {
        if (typeof data.points !== 'number' || isNaN(data.points) || data.points < 0) {
          throw new Error('Invalid points: must be a non-negative number');
        }
      }
      
      return true;
    }
    
    /**
     * Sanitize error message for production to prevent information leakage
     * @param {Error} error - The original error object
     * @param {string} functionName - The name of the function that failed
     * @returns {Error} A sanitized error with generic message in production
     */
    _sanitizeError(error, functionName) {
      const isDevelopment = window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1' ||
                           window.location.hostname.includes('dev');
      
      if (isDevelopment) {
        return error;
      }
      
      // In production, return generic error messages
      if (error.message && error.message.includes('Failed to fetch')) {
        return new Error('Network error. Please check your connection and try again.');
      }
      
      if (error.message && error.message.includes('timeout')) {
        return new Error('Request timed out. Please try again.');
      }
      
      if (error.message && error.message.includes('status')) {
        return new Error('Service temporarily unavailable. Please try again later.');
      }
      
      return new Error('An error occurred. Please try again.');
    }
    
    /**
     * Rate limit function calls to prevent excessive API requests
     * Enforces minimum interval between requests and maximum concurrent requests
     * @returns {Promise<void>}
     */
    async _rateLimit() {
      const now = Date.now();
      const timeSinceLastRequest = now - this.lastRequestTime;
      
      if (timeSinceLastRequest < this.minRequestInterval) {
        await new Promise(resolve => 
          setTimeout(resolve, this.minRequestInterval - timeSinceLastRequest)
        );
      }
      
      // Wait if too many concurrent requests
      while (this.activeRequests >= this.maxConcurrentRequests) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      
      this.lastRequestTime = Date.now();
      this.activeRequests++;
    }
    
    /**
     * Call Firebase Function with enhanced error handling, validation, and rate limiting
     * @param {string} functionName - The name of the Firebase function to call
     * @param {Object} data - The data to pass to the function (default: {})
     * @param {Object} options - Additional options for the function call (default: {})
     * @param {number} options.maxRetries - Maximum number of retry attempts (default: 3)
     * @param {number} options.retryDelay - Initial retry delay in ms (default: 1000)
     * @returns {Promise<Object>} The response from the Firebase function
     * @throws {Error} If the function call fails after all retries
     */
    async callFunction(functionName, data = {}, options = {}) {
      // Validate input parameters
      try {
        this._validateFunctionCall(functionName, data);
      } catch (validationError) {
        const sanitizedError = this._sanitizeError(validationError, functionName);
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          console.error('Function call validation error:', validationError);
        }
        throw sanitizedError;
      }
      
      if (!this.initialized) {
        try {
          await this.initialize();
        } catch (initError) {
          const sanitizedError = this._sanitizeError(initError, functionName);
          if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            console.error('Firebase initialization error:', initError);
          }
          throw sanitizedError;
        }
      }

      // Apply rate limiting
      await this._rateLimit();
      
      const maxRetries = options.maxRetries || 3;
      const retryDelay = options.retryDelay || 1000;
      let lastError = null;
      
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          const url = `${this.functionsBaseUrl}/${functionName}`;
          
          // Create abort controller for timeout
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), this.requestTimeout);
          
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ data }),
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            // Don't retry on client errors (4xx), only server errors (5xx)
            if (response.status >= 400 && response.status < 500) {
              this.activeRequests--;
              const error = new Error(`Function call failed: ${response.status}`);
              throw this._sanitizeError(error, functionName);
            }
            
            // Retry on server errors
            throw new Error(`Function call failed: ${response.status}`);
          }

          const result = await response.json();
          this.activeRequests--;
          return result;
          
        } catch (error) {
          lastError = error;
          
          // Don't retry on abort (timeout) or validation errors
          if (error.name === 'AbortError') {
            this.activeRequests--;
            throw this._sanitizeError(new Error('Request timeout'), functionName);
          }
          
          // Exponential backoff for retries
          if (attempt < maxRetries - 1) {
            const delay = retryDelay * Math.pow(2, attempt);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
      
      // All retries failed
      this.activeRequests--;
      const sanitizedError = this._sanitizeError(lastError, functionName);
      
      // Only log detailed errors in development
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        console.error(`Error calling function ${functionName} after ${maxRetries} attempts:`, lastError);
      }
      
      throw sanitizedError;
    }

    /**
     * Loyalty System Methods
     */
    
    /**
     * Get customer loyalty data
     * @param {string} customerId - The customer ID to look up
     * @returns {Promise<Object>} Customer loyalty data
     * @throws {Error} If customerId is invalid
     */
    async getCustomerLoyalty(customerId) {
      if (!customerId || typeof customerId !== 'string' || customerId.trim().length === 0) {
        throw new Error('Invalid customerId: must be a non-empty string');
      }
      return await this.callFunction('getCustomerLoyalty', { customerId });
    }

    /**
     * Update customer loyalty points
     * @param {string} customerId - The customer ID
     * @param {number} points - The points to add (must be non-negative)
     * @param {Object} orderData - Optional order data associated with the points (default: {})
     * @returns {Promise<Object>} Updated customer loyalty data
     * @throws {Error} If parameters are invalid
     */
    async updateCustomerPoints(customerId, points, orderData = {}) {
      if (!customerId || typeof customerId !== 'string' || customerId.trim().length === 0) {
        throw new Error('Invalid customerId: must be a non-empty string');
      }
      if (typeof points !== 'number' || isNaN(points) || points < 0) {
        throw new Error('Invalid points: must be a non-negative number');
      }
      if (orderData && typeof orderData !== 'object') {
        throw new Error('Invalid orderData: must be an object');
      }
      return await this.callFunction('updateCustomerPoints', { customerId, points, orderData });
    }

    /**
     * Redeem customer loyalty points
     * @param {string} customerId - The customer ID
     * @param {number} pointsToRedeem - The points to redeem (must be positive)
     * @returns {Promise<Object>} Updated customer loyalty data
     * @throws {Error} If parameters are invalid
     */
    async redeemPoints(customerId, pointsToRedeem) {
      if (!customerId || typeof customerId !== 'string' || customerId.trim().length === 0) {
        throw new Error('Invalid customerId: must be a non-empty string');
      }
      if (typeof pointsToRedeem !== 'number' || isNaN(pointsToRedeem) || pointsToRedeem <= 0) {
        throw new Error('Invalid pointsToRedeem: must be a positive number');
      }
      return await this.callFunction('redeemPoints', { customerId, pointsToRedeem });
    }

    /**
     * Get customer loyalty summary
     * @param {string} customerId - The customer ID
     * @returns {Promise<Object>} Customer loyalty summary
     * @throws {Error} If customerId is invalid
     */
    async getLoyaltySummary(customerId) {
      if (!customerId || typeof customerId !== 'string' || customerId.trim().length === 0) {
        throw new Error('Invalid customerId: must be a non-empty string');
      }
      return await this.callFunction('getLoyaltySummary', { customerId });
    }

    /**
     * Alert System Methods
     */
    
    /**
     * Get VAPID public key for push notifications
     * @returns {Promise<Object>} VAPID public key
     */
    async getVapidPublicKey() {
      return await this.callFunction('getVapidPublicKey', {});
    }

    /**
     * Send email notification
     * @param {string} subscriptionId - The subscription ID
     * @param {string} notificationType - The type of notification
     * @param {Object} productData - Optional product data (default: undefined)
     * @returns {Promise<Object>} Notification result
     * @throws {Error} If parameters are invalid
     */
    async sendEmailNotification(subscriptionId, notificationType, productData) {
      if (!subscriptionId || typeof subscriptionId !== 'string' || subscriptionId.trim().length === 0) {
        throw new Error('Invalid subscriptionId: must be a non-empty string');
      }
      if (!notificationType || typeof notificationType !== 'string') {
        throw new Error('Invalid notificationType: must be a string');
      }
      if (productData && typeof productData !== 'object') {
        throw new Error('Invalid productData: must be an object');
      }
      return await this.callFunction('sendEmailNotification', {
        subscriptionId,
        notificationType,
        productData
      });
    }

    /**
     * Send push notification
     * @param {string} subscriptionId - The subscription ID
     * @param {string} notificationType - The type of notification
     * @param {Object} productData - Optional product data (default: undefined)
     * @returns {Promise<Object>} Notification result
     * @throws {Error} If parameters are invalid
     */
    async sendPushNotification(subscriptionId, notificationType, productData) {
      if (!subscriptionId || typeof subscriptionId !== 'string' || subscriptionId.trim().length === 0) {
        throw new Error('Invalid subscriptionId: must be a non-empty string');
      }
      if (!notificationType || typeof notificationType !== 'string') {
        throw new Error('Invalid notificationType: must be a string');
      }
      if (productData && typeof productData !== 'object') {
        throw new Error('Invalid productData: must be an object');
      }
      return await this.callFunction('sendPushNotification', {
        subscriptionId,
        notificationType,
        productData
      });
    }

    /**
     * Firestore Methods
     */
    async getDocuments(collectionName, options = {}) {
      if (!this.initialized) {
        throw new Error('Firebase not initialized');
      }

      try {
        const { collection, query, where, orderBy, limit, getDocs } = await import('https://www.gstatic.com/firebasejs/10.7.0/firebase-firestore.js');
        
        let q = collection(this.db, collectionName);
        
        // Apply where conditions
        if (options.where && Array.isArray(options.where)) {
          options.where.forEach(condition => {
            if (condition.length >= 2) {
              const fieldPath = condition[0];
              const operator = condition[1];
              const value = condition[2];
              q = where(q, fieldPath, operator, value);
            }
          });
        }
        
        // Apply order by
        if (options.orderBy) {
          const direction = options.orderDirection || 'asc';
          q = orderBy(q, options.orderBy, direction);
        }
        
        // Apply limit
        if (options.limit) {
          q = limit(q, options.limit);
        }
        
        const snapshot = await getDocs(q);
        const documents = [];
        
        snapshot.forEach(doc => {
          documents.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        return documents;
      } catch (error) {
        console.error('Error getting documents:', error);
        // Return empty array instead of throwing error for better UX
        return [];
      }
    }

    async setDocument(collectionName, data, documentId = null) {
      if (!this.initialized) {
        throw new Error('Firebase not initialized');
      }

      try {
        const { doc, setDoc, collection } = await import('https://www.gstatic.com/firebasejs/10.7.0/firebase-firestore.js');
        
        const docRef = documentId 
          ? doc(collection(this.db, collectionName), documentId)
          : doc(collection(this.db, collectionName));
        
        await setDoc(docRef, {
          ...data,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
        
        return docRef.id;
      } catch (error) {
        console.error('Error setting document:', error);
        // Return a fallback ID instead of throwing error
        return documentId || `temp_${Date.now()}`;
      }
    }

    async updateDocument(collectionName, documentId, data) {
      if (!this.initialized) {
        throw new Error('Firebase not initialized');
      }

      try {
        const { doc, updateDoc, collection } = await import('https://www.gstatic.com/firebasejs/10.7.0/firebase-firestore.js');
        
        const docRef = doc(collection(this.db, collectionName), documentId);
        
        await updateDoc(docRef, {
          ...data,
          updated_at: new Date().toISOString()
        });
        
        return true;
      } catch (error) {
        console.error('Error updating document:', error);
        throw error;
      }
    }

    async deleteDocument(collectionName, documentId) {
      if (!this.initialized) {
        throw new Error('Firebase not initialized');
      }

      try {
        const { doc, deleteDoc, collection } = await import('https://www.gstatic.com/firebasejs/10.7.0/firebase-firestore.js');
        
        const docRef = doc(collection(this.db, collectionName), documentId);
        await deleteDoc(docRef);
        
        return true;
      } catch (error) {
        console.error('Error deleting document:', error);
        throw error;
      }
    }

    /**
     * Analytics Methods
     */
    async trackAnalytics(eventName, eventData = {}) {
      if (!this.initialized) {
        console.warn('Firebase not initialized, skipping analytics');
        return;
      }

      try {
        // Store analytics in Firestore
        await this.setDocument('analytics_events', {
          event_name: eventName,
          event_data: eventData,
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent,
          url: window.location.href
        });

        // Analytics tracked successfully
      } catch (error) {
        console.error('Error tracking analytics:', error);
        // Don't throw error, just log it for better UX
      }
    }

    /**
     * Update configuration from environment variables
     */
    updateConfig() {
      if (window.__ENV__) {
        this.config = {
          apiKey: window.__ENV__.FIREBASE_API_KEY || "",
          authDomain: window.__ENV__.FIREBASE_AUTH_DOMAIN || "",
          projectId: window.__ENV__.FIREBASE_PROJECT_ID || "",
          storageBucket: window.__ENV__.FIREBASE_STORAGE_BUCKET || "",
          messagingSenderId: window.__ENV__.FIREBASE_MESSAGING_SENDER_ID || "",
          appId: window.__ENV__.FIREBASE_APP_ID || ""
        };
        
        this.functionsBaseUrl = `https://us-central1-${window.__ENV__.FIREBASE_PROJECT_ID || ''}.cloudfunctions.net`;
      }
    }

    /**
     * Utility Methods
     */
    getConfig() {
      return this.config;
    }

    getServices() {
      return this.services;
    }

    isInitialized() {
      return this.initialized;
    }

    dispatchEvent(eventName, data = {}) {
      const event = new CustomEvent(`kt-firebase-${eventName}`, {
        detail: data,
        bubbles: true
      });
      document.dispatchEvent(event);
    }

    addEventListener(eventName, callback) {
      document.addEventListener(`kt-firebase-${eventName}`, callback);
    }

    removeEventListener(eventName, callback) {
      document.removeEventListener(`kt-firebase-${eventName}`, callback);
    }
  }

  // Create global instance
  window.KTUnifiedFirebase = new UnifiedFirebaseManager();

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.KTUnifiedFirebase.initialize();
    });
  } else {
    window.KTUnifiedFirebase.initialize();
  }

  // Firebase Manager loaded and ready

})();
